import { createContext, useContext } from 'react';
import type { ComponentType } from 'react';
import type { PaginationProps } from 'antd';

import type { AccountView } from '@skynet/data-shared-accounts';

/** Props of {@link ServersSlots.AccountList}: one server's accounts in its expanded row. */
export interface ServerAccountListSlotProps {
  /** Heading above the list. */
  title: string;
  /** The list's pagination, or `false` to show every account. */
  pagination: PaginationProps | false;
  /** The server's accounts, already narrowed by the table search. */
  accounts: AccountView[];
  /** The server (and exchange) id the accounts belong to. */
  exchangeID: number;
  searchText: string;
  /** Set only when the operator may enable accounts. */
  onEnable?: (account: AccountView) => void;
  /** Set only when the operator may disable accounts. */
  onDisable?: (account: AccountView) => void;
  /** Opens (`expanded`) or closes an account's details; `null` closes them all. */
  onShowDetails: (expanded: boolean, account: AccountView | null) => void;
}

/** Props of {@link ServersSlots.AccountDetails}. */
export interface ServerAccountDetailsSlotProps {
  /** The account whose details are open, with its server id; `null` when closed. */
  account: (AccountView & { exchange: number }) | null;
  onClose: () => void;
}

/**
 * Pieces of the Servers page the dashboard still owns: the account list and the
 * account details (with its transactions) are shared with other dashboard pages
 * and are not packaged yet. The app passes them in through
 * {@link ServersSlotsProvider} around the Servers route.
 */
export interface ServersSlots {
  AccountList: ComponentType<ServerAccountListSlotProps>;
  AccountDetails: ComponentType<ServerAccountDetailsSlotProps>;
  /** Loads the transactions of the account whose details were opened. */
  loadAccountTransactions: (accountId: string) => void;
  /** Drops the loaded transactions once the details are closed. */
  resetAccountTransactions: () => void;
}

const ServersSlotsContext = createContext<ServersSlots | null>(null);

/** Supplies the app-owned {@link ServersSlots} to the Servers page. */
export const ServersSlotsProvider = ServersSlotsContext.Provider;

/** The {@link ServersSlots} of the enclosing provider; throws without one. */
export function useServersSlots(): ServersSlots {
  const slots = useContext(ServersSlotsContext);
  if (!slots) {
    throw new Error('ServersPage must be rendered inside a ServersSlotsProvider');
  }
  return slots;
}
